import React, { useState } from 'react';
import { Grid } from '@material-ui/core';

import {
  Site,
  User,
  ReportConfiguration,
  GenerateReportRequest,
  ExportReportRequest,
  ReportType,
  ReportMenu,
  ReportMenuType,
} from '../../../models';

import ReportTypeMenu from './ReportTypeMenu';
import ReportConfigurationForm from './ReportConfigurationForm';
import ReportConfirmationModal from './ReportConfirmationModal';
import ExportConfigurationForm from './ExportConfigurationForm';
import ExportConfirmationModal from './ExportConfirmationModal';

enum ReportsPageAction {
  NONE,
  GENERATE,
  EXPORT,
}

export interface Props {
  email: string;
  reportConfigurations: ReportConfiguration[];
  site: Site;
  siteId: number;
  users: User[];
}

const ReportsPage: React.FC<Props> = ({ email, reportConfigurations, site, siteId, users }) => {
  const [activeMenuItem, setActiveMenuItem] = useState<ReportMenuType | ''>('');

  const [action, setAction] = useState<ReportsPageAction>(ReportsPageAction.NONE);
  const [actionRequest, setActionRequest] = useState<GenerateReportRequest | ExportReportRequest>();
  const [submitCounter, setSubmitCounter] = useState(0);

  const handleReportMenuChange = (type: ReportMenuType) => {
    setActiveMenuItem(type);
  };

  const handleDismissActionModal = (clearForm: boolean) => {
    setAction(ReportsPageAction.NONE);
    if (clearForm) {
      setSubmitCounter((prev) => prev + 1);
    }
  };

  const handleShowGenerateConfirmationModal = (request: GenerateReportRequest) => {
    setAction(ReportsPageAction.GENERATE);
    setActionRequest(request);
  };

  const handleShowExportConfirmationModal = (request: ExportReportRequest) => {
    setAction(ReportsPageAction.EXPORT);
    setActionRequest(request);
  };

  const activeConfiguration = reportConfigurations.reduce((acc, config) => {
    if (config.reportName === activeMenuItem) {
      return config;
    }
    return acc;
  });

  return (
    <div data-testid="reports-page">
      <ReportTypeMenu
        configurations={reportConfigurations}
        onReportTypeChange={handleReportMenuChange}
        site={site}
      />
      <Grid item style={{ position: 'relative', flex: 1, padding: 16, paddingBottom: 0 }}>
        {Object.values(ReportType).includes(activeMenuItem as ReportType) && (
          <ReportConfigurationForm
            key={`${activeMenuItem}-${submitCounter}`}
            site={site}
            availableTimePeriods={activeConfiguration.availableTimePeriods}
            reportName={activeConfiguration.reportName}
            reportId={activeConfiguration.reportId}
            onSubmit={handleShowGenerateConfirmationModal}
            users={users.map((user) => user.emailAddress)}
            email={email}
            closeForm={() => setActiveMenuItem('')}
          />
        )}
        {activeMenuItem === ReportMenu.EXPORT && (
          <ExportConfigurationForm
            submitCounter={submitCounter}
            siteId={siteId}
            onSubmit={handleShowExportConfirmationModal}
            site={site}
            users={users.map((user) => user.emailAddress)}
            email={email}
            closeForm={() => setActiveMenuItem('')}
          />
        )}
      </Grid>
      {action === ReportsPageAction.GENERATE && actionRequest && (
        <ReportConfirmationModal
          reportRequest={actionRequest as GenerateReportRequest}
          onCancel={handleDismissActionModal}
        />
      )}
      {action === ReportsPageAction.EXPORT && actionRequest && (
        <ExportConfirmationModal
          exportRequest={actionRequest as ExportReportRequest}
          onCancel={handleDismissActionModal}
        />
      )}
    </div>
  );
};

export default ReportsPage;
